import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ZoomIn } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";

const PHOTOS = [
    {
        url: "https://images.unsplash.com/photo-1635424824800-692767998d07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwzfHxyb29mJTIwY29uc3RydWN0aW9uJTIwd29ya2VyfGVufDB8fHx8MTc5MDkzODEwOHww&ixlib=rb-4.1.0&q=85",
        caption: "Sheet fixing with precision fasteners",
        tag: "Installations",
        span: "md:col-span-2 md:row-span-2",
    },
    {
        url: "https://images.unsplash.com/photo-1587582423116-ec07293f0395?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxyb29mJTIwY29uc3RydWN0aW9uJTIwd29ya2VyfGVufDB8fHx8MTc5MDkzODEwOHww&ixlib=rb-4.1.0&q=85",
        caption: "Truss framing and roof alignment",
        tag: "Installations",
        span: "",
    },
    {
        url: "https://images.unsplash.com/photo-1602193458517-db6caca8f1fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxtZXRhbCUyMHJvb2YlMjBob3VzZXxlbnwwfHx8fDE3OTA5MzgxMDh8MA&ixlib=rb-4.1.0&q=85",
        caption: "Standing seam finish, ready for the rains",
        tag: "Installations",
        span: "",
    },
    {
        url: "https://images.unsplash.com/photo-1623489254637-a2dd8375243d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxyb29mJTIwY29uc3RydWN0aW9uJTIwd29ya2VyfGVufDB8fHx8MTc5MDkzODEwOHww&ixlib=rb-4.1.0&q=85",
        caption: "Gutter and storm drainage system",
        tag: "Gutters",
        span: "",
    },
    {
        url: "https://images.unsplash.com/photo-1591474200742-8e512e6f98f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMHJvb2YlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzkwOTM4MTE0fDA&ixlib=rb-4.1.0&q=85",
        caption: "Completed residential roofscape",
        tag: "Installations",
        span: "",
    },
    {
        url: "https://images.unsplash.com/photo-1587061633437-187ac80e8e7a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMHJvb2YlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzkwOTM4MTE0fDA&ixlib=rb-4.1.0&q=85",
        caption: "Ridge cap and leak-proofing detail",
        tag: "Repairs",
        span: "",
    },
];

const FILTERS = ["All", "Installations", "Repairs", "Gutters"];

export const Gallery = () => {
    const [filter, setFilter] = useState("All");
    const [lightbox, setLightbox] = useState(null);
    const shown = PHOTOS.filter((p) => filter === "All" || p.tag === filter);

    return (
        <section id="projects" data-testid="craft-photo-gallery" className="py-24 sm:py-32">
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-12">
                    <FadeUp>
                        <Eyebrow>On the roof</Eyebrow>
                        <h2 className="font-display font-bold uppercase text-4xl sm:text-6xl leading-[1.02] mt-5">
                            The craft,<br />
                            <span className="text-outline">up close.</span>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.1} className="flex flex-wrap gap-2">
                        {FILTERS.map((f) => (
                            <button
                                key={f}
                                data-testid={`gallery-filter-${f.toLowerCase()}`}
                                onClick={() => setFilter(f)}
                                className={`font-mono2 text-[11px] uppercase tracking-[0.15em] px-4 py-2 border transition-colors ${
                                    filter === f
                                        ? "bg-amber-500 text-[#0F172A] border-amber-500 font-semibold"
                                        : "border-white/15 text-[#94A3B8] hover:text-white hover:border-white/40"
                                }`}
                            >
                                {f}
                            </button>
                        ))}
                    </FadeUp>
                </div>

                <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 auto-rows-[240px] md:auto-rows-[250px] gap-4">
                    <AnimatePresence mode="popLayout">
                        {shown.map((p, i) => (
                            <motion.button
                                layout
                                key={p.caption}
                                data-testid={`gallery-item-${i}`}
                                initial={{ opacity: 0, scale: 0.96 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.96 }}
                                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                onClick={() => setLightbox(p)}
                                className={`relative overflow-hidden border border-white/10 group text-left ${p.span}`}
                            >
                                <img
                                    src={p.url}
                                    alt={p.caption}
                                    loading="lazy"
                                    className="absolute inset-0 w-full h-full object-cover img-treated transition-transform duration-700 group-hover:scale-[1.07]"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F12]/95 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-[radial-gradient(circle_at_50%_40%,rgba(245,158,11,0.18),transparent_65%)]" />
                                <div className="absolute bottom-0 inset-x-0 p-4 flex items-end justify-between gap-2">
                                    <p className="text-sm font-medium text-white">{p.caption}</p>
                                    <span className="text-amber-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <ZoomIn size={17} />
                                    </span>
                                </div>
                                <span className="absolute top-3 left-3 font-mono2 text-[9px] uppercase tracking-[0.2em] text-white/70 border border-white/20 bg-[#0B0F12]/60 backdrop-blur px-2 py-1">
                                    {p.tag}
                                </span>
                            </motion.button>
                        ))}
                    </AnimatePresence>
                </motion.div>
            </div>

            <AnimatePresence>
                {lightbox && (
                    <motion.div
                        data-testid="gallery-lightbox-modal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[85] bg-[#0B0F12]/95 backdrop-blur-md flex items-center justify-center p-5"
                        onClick={() => setLightbox(null)}
                    >
                        <motion.figure
                            initial={{ scale: 0.94, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.94, y: 20 }}
                            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                            className="max-w-4xl w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <img src={lightbox.url} alt={lightbox.caption} className="w-full max-h-[75vh] object-cover border border-white/15" />
                            <figcaption className="flex items-center justify-between mt-4">
                                <span className="text-white">{lightbox.caption}</span>
                                <button
                                    data-testid="gallery-lightbox-close"
                                    onClick={() => setLightbox(null)}
                                    className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white border border-white/20 px-4 py-2 transition-colors"
                                >
                                    <X size={15} /> Close
                                </button>
                            </figcaption>
                        </motion.figure>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default Gallery;
